/**
 * iter200 — Trip Excel/CSV import sheet.
 *
 * Three-stage flow inside a Shadcn bottom-sheet:
 *   1. UPLOAD     → user drops a .xlsx/.csv
 *   2. PREVIEW    → table of parsed rows with errors highlighted +
 *                    column-mapping panel + summary card
 *   3. CONFIRM    → POST rows to /import/confirm → toast + close
 *
 * Backend already does the heavy lifting (parsing, phone resolution,
 * member validation). This component is a thin UI shell.
 */
import { useState, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function TripImportSheet({ open, onOpenChange, groupId, groupName, onImported }) {
  const [stage, setStage] = useState('upload'); // upload | preview | confirming
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStage('upload');
    setFile(null);
    setPreview(null);
  };

  const handleClose = () => {
    if (confirming) return;
    reset();
    onOpenChange(false);
  };

  // iter200.1 — Download a pre-filled .xlsx template that already has the
  // right headers + 2 sample rows using the trip's actual member phones.
  // Zero ambiguity about "what columns do I need?".
  const handleDownloadTemplate = async () => {
    if (!groupId) return;
    setDownloadingTemplate(true);
    try {
      const res = await api.get(`/trips/${groupId}/import/template`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      // Try to recover the filename from Content-Disposition; fall back to a
      // safe default if the header was stripped by the browser/proxy.
      let filename = `${(groupName || 'trip').replace(/[^A-Za-z0-9_-]/g, '_')}_expense_template.xlsx`;
      const disp = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      const m = disp && disp.match(/filename="?([^"]+)"?/i);
      if (m) filename = m[1];
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post(`/trips/${groupId}/import/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setStage('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse file');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.errors.length === 0);
    if (validRows.length === 0) {
      toast.error('No valid rows to import — fix errors first');
      return;
    }
    setConfirming(true);
    try {
      const payload = {
        rows: validRows.map((r) => ({
          description: r.description,
          amount: r.amount,
          paid_by_user_id: r.paid_by_user_id,
          split_among: r.split_among,
          date: r.date,
          category: r.category,
          currency: r.currency,
        })),
        default_category: 'Other',
      };
      const res = await api.post(`/trips/${groupId}/import/confirm`, payload);
      toast.success(
        `Imported ${res.data.created} expense(s)${res.data.auto_approved ? ' (auto-approved)' : ' — pending approval'}`,
      );
      onImported?.(res.data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border-t-2 px-0 pt-4 pb-8 max-h-[92vh] overflow-y-auto"
        data-testid="trip-import-sheet"
      >
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-muted-foreground/25" />
        <SheetHeader className="px-5 text-left">
          <SheetTitle className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Import expenses from Excel
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {groupName} — bulk-add up to 500 rows
          </SheetDescription>
        </SheetHeader>

        {/* ----- STAGE: UPLOAD ----- */}
        {stage === 'upload' && (
          <div className="px-5 mt-5 space-y-4" data-testid="trip-import-stage-upload">
            <Card className="p-6 border-2 border-dashed rounded-2xl text-center">
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-semibold mb-1">Drop a file or tap to choose</p>
              <p className="text-xs text-muted-foreground mb-4">
                .xlsx or .csv — max 10 MB, 500 rows
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                data-testid="trip-import-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-full"
                data-testid="trip-import-choose-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Choose file
              </Button>
            </Card>

            <Card className="p-4 border-2 rounded-2xl space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Expected columns
              </p>
              <ul className="text-xs space-y-1 text-muted-foreground leading-snug">
                <li>• <strong>Description</strong> — what was purchased</li>
                <li>• <strong>Amount</strong> — number, e.g. <code>1850</code> or <code>Rs 1,850</code></li>
                <li>• <strong>Paid By</strong> — mobile number (10 digits, with or without +91)</li>
                <li>• <strong>Split Among</strong> (required) — comma-separated mobile numbers, or <code>ALL</code></li>
                <li>• <em>Date</em>, <em>Category</em>, <em>Currency</em> — optional</li>
              </ul>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                data-testid="trip-import-download-template-btn"
                className="rounded-full w-full mt-3 border-2 border-dashed border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 text-xs"
              >
                {downloadingTemplate ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                )}
                Download a starter template (.xlsx)
              </Button>
              <p className="text-xs text-muted-foreground/80 text-center italic">
                Pre-filled with your trip members&apos; phones + 2 example rows
              </p>
            </Card>
          </div>
        )}

        {/* ----- STAGE: PREVIEW ----- */}
        {stage === 'preview' && preview && (
          <div className="px-3 mt-5 space-y-4" data-testid="trip-import-stage-preview">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2 px-2">
              <Card className="p-3 rounded-xl border-2 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total rows</p>
                <p className="text-lg font-bold" data-testid="trip-import-total">{preview.summary.total_rows}</p>
              </Card>
              <Card className="p-3 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 text-center">
                <p className="text-xs uppercase tracking-wider text-emerald-700">Valid</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300" data-testid="trip-import-valid">
                  {preview.summary.valid_rows}
                </p>
              </Card>
              <Card className={`p-3 rounded-xl border-2 text-center ${
                preview.summary.error_rows > 0
                  ? 'border-red-500/40 bg-red-500/5'
                  : ''
              }`}>
                <p className={`text-xs uppercase tracking-wider ${
                  preview.summary.error_rows > 0 ? 'text-red-700' : 'text-muted-foreground'
                }`}>Errors</p>
                <p className={`text-lg font-bold ${
                  preview.summary.error_rows > 0 ? 'text-red-700 dark:text-red-300' : ''
                }`} data-testid="trip-import-errors">
                  {preview.summary.error_rows}
                </p>
              </Card>
            </div>
            <p className="text-center text-xs text-muted-foreground" data-testid="trip-import-total-amount">
              Total amount: <strong>₹{preview.summary.total_amount.toLocaleString('en-IN')}</strong>
            </p>

            {/* Column mapping */}
            <Card className="mx-2 p-4 border-2 rounded-2xl space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Detected columns
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {Object.entries(preview.mapping).map(([canonical, header]) => (
                  <div key={canonical} className="flex justify-between items-baseline gap-1">
                    <span className="text-muted-foreground capitalize">{canonical.replace(/_/g, ' ')}</span>
                    <code className="font-mono text-xs truncate">{header || '—'}</code>
                  </div>
                ))}
              </div>
            </Card>

            {/* Row list */}
            <div className="mx-2 space-y-2">
              {preview.rows.map((r) => (
                <div
                  key={r.sheet_row}
                  className={`rounded-xl border-2 p-3 ${
                    r.errors.length > 0
                      ? 'border-red-500/40 bg-red-500/5'
                      : 'border-border bg-card'
                  }`}
                  data-testid={`trip-import-row-${r.sheet_row}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-muted-foreground font-mono">row {r.sheet_row}</span>
                        {r.errors.length === 0 ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                        )}
                      </div>
                      <p className="text-sm font-semibold truncate">
                        {r.description || <span className="italic text-muted-foreground">(no description)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ₹{r.amount?.toLocaleString('en-IN') ?? '?'} · paid by{' '}
                        {r.paid_by_name || <span className="text-red-600">?</span>} · split {r.split_among.length}
                        {r.date && ` · ${r.date}`}
                      </p>
                    </div>
                  </div>
                  {r.errors.length > 0 && (
                    <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-0.5">
                      {r.errors.map((e, i) => (
                        <li key={i}>• {e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Confirm bar */}
            <div className="sticky bottom-0 left-0 right-0 px-3 pt-3 bg-background border-t-2 border-border/60 -mx-3">
              <div className="flex gap-2 px-2">
                <Button
                  variant="outline"
                  onClick={() => { reset(); }}
                  disabled={confirming}
                  className="rounded-full flex-1"
                  data-testid="trip-import-back-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Start over
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={confirming || preview.summary.valid_rows === 0}
                  className="rounded-full flex-[2]"
                  data-testid="trip-import-confirm-btn"
                >
                  {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Import {preview.summary.valid_rows} expense{preview.summary.valid_rows === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
